import './bootstrap';
import React, { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import AppLayout from '@/components/features/AppLayout';
import AppErrorBoundary from '@/components/ui/AppErrorBoundary';
import Home from '@/Pages/Home';
import Menu from '@/components/features/Menu/Menu';
import Contact from '@/Pages/Contact';
import NotFound from '@/Pages/NotFound';
import LegalPage from '@/Pages/LegalPage';
import Services from '@/Pages/Services';
import AboutPage from '@/Pages/AboutPage';
import FoodDetailPage from '@/Pages/Menu/FoodDetailPage';
import MenuDetailPage from '@/Pages/Menu/MenuDetailPage';
import CheckoutPage from '@/Pages/CheckoutPage';
import OrderSuccessPage from '@/Pages/OrderSuccessPage';
import CreateMenuPage from '@/Pages/CreateMenuPage';
import CartSSEWatcher from '@/components/features/Cart/CartSSEWatcher';

import { CartProvider } from '@/Context/CartContext';
import { ToastProvider } from '@/Context/ToastContext';
import { ThemeProvider } from '@/Context/ThemeContext';
import { HelmetProvider } from 'react-helmet-async';
import content from '@/data/content';
import { initAnalytics, trackPageView } from '@/Services/analytics';
import { reportError } from '@/Services/errorReporter';

const ONE_MINUTE_MS = 1000 * 60;

const GlobalErrorReporter = () => {
    useEffect(() => {
        const onError = (event: ErrorEvent) => {
            reportError({
                message: event.message,
                source: 'window.error',
                stack: event.error?.stack,
            });
        };
        const onRejection = (event: PromiseRejectionEvent) => {
            reportError({
                message: event.reason?.message ?? 'Unhandled promise rejection',
                source: 'window.unhandledrejection',
                stack: event.reason?.stack,
            });
        };
        window.addEventListener('error', onError);
        window.addEventListener('unhandledrejection', onRejection);
        return () => {
            window.removeEventListener('error', onError);
            window.removeEventListener('unhandledrejection', onRejection);
        };
    }, []);

    return null;
};

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: ONE_MINUTE_MS,
            refetchOnWindowFocus: false,
            retry: 1,
        },
    },
});

const RouteTracker = () => {
    const location = useLocation();

    useEffect(() => {
        trackPageView(`${location.pathname}${location.search}`);
    }, [location.pathname, location.search]);

    return null;
};

// Scrolls to top on route change — distinct from the floating back-to-top button in ui/ScrollToTop
const RouteChangeScrollToTop = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return null;
};

interface MaintenanceData {
    title?: string;
    message?: string;
    contact_email?: string;
}

const MaintenanceGate = ({ children }: { children: React.ReactNode }) => {
    const [isMaintenance, setIsMaintenance] = React.useState<boolean | null>(null);
    const [maintenanceData, setMaintenanceData] = React.useState<MaintenanceData | null>(null);

    useEffect(() => {
        try {
            if (window.localStorage.getItem('cookie_consent_v1') === 'accepted') {
                initAnalytics();
            }
        } catch {
            // storage unavailable
        }
    }, []);

    useEffect(() => {
        if ((content as { active?: boolean }).active === false) {
            import('../../public/data/maintenance.json')
                .then((mod) => {
                    setMaintenanceData((mod.default || mod) as MaintenanceData);
                    setIsMaintenance(true);
                })
                .catch(() => {
                    setIsMaintenance(true);
                });
        } else {
            setIsMaintenance(false);
        }
    }, []);

    if (isMaintenance === null) return null;

    if (isMaintenance) {
        const data = maintenanceData || {};
        return (
            <div
                className="flex flex-col items-center justify-center h-screen bg-gray-50 p-5 text-center font-sans"
            >
                <h1 className="text-5xl font-black text-gray-900 mb-4">
                    {data.title || 'Site en maintenance'}
                </h1>
                <p className="text-xl text-gray-600 max-w-lg mb-8">
                    {data.message || 'Nous revenons très vite.'}
                </p>
                {data.contact_email && (
                    <p className="text-gray-500">
                        Contact:{' '}
                        <a
                            href={`mailto:${data.contact_email}`}
                            className="text-orange-600"
                        >
                            {data.contact_email}
                        </a>
                    </p>
                )}
            </div>
        );
    }

    return <>{children}</>;
};

const root = document.getElementById('app-root');

if (root) {
    ReactDOM.createRoot(root).render(
        <React.StrictMode>
            <MaintenanceGate>
                <QueryClientProvider client={queryClient}>
                    <AppErrorBoundary>
                        <GlobalErrorReporter />
                        <ToastProvider>
                            <CartProvider>
                                <CartSSEWatcher />
                                <ThemeProvider>
                                    <BrowserRouter>
                                        <HelmetProvider>
                                            <RouteChangeScrollToTop />
                                            <RouteTracker />
                                            <Routes>
                                            <Route path="/" element={<AppLayout />}>
                                                <Route index element={<AppErrorBoundary><Home /></AppErrorBoundary>} />
                                                <Route path="menu" element={<AppErrorBoundary><Menu /></AppErrorBoundary>} />
                                                <Route path="menu/:slug" element={<AppErrorBoundary><FoodDetailPage /></AppErrorBoundary>} />
                                                <Route path="menus/:slug" element={<AppErrorBoundary><MenuDetailPage /></AppErrorBoundary>} />
                                                <Route path="contact" element={<AppErrorBoundary><Contact /></AppErrorBoundary>} />
                                                <Route path="404" element={<NotFound />} />
                                                <Route path="about-us" element={<AppErrorBoundary><AboutPage /></AppErrorBoundary>} />
                                                <Route path="services" element={<AppErrorBoundary><Services /></AppErrorBoundary>} />
                                                <Route
                                                    path="mentions-legales"
                                                    element={<AppErrorBoundary><LegalPage slug="mentions-legales" /></AppErrorBoundary>}
                                                />
                                                <Route
                                                    path="politique-confidentialite"
                                                    element={<AppErrorBoundary><LegalPage slug="politique-confidentialite" /></AppErrorBoundary>}
                                                />
                                                <Route path="cgv" element={<AppErrorBoundary><LegalPage slug="cgv" /></AppErrorBoundary>} />
                                                <Route path="checkout" element={<AppErrorBoundary><CheckoutPage /></AppErrorBoundary>} />
                                                <Route path="order/success" element={<AppErrorBoundary><OrderSuccessPage /></AppErrorBoundary>} />
                                                <Route
                                                    path="order/cancelled"
                                                    element={<Navigate to="/checkout" replace />}
                                                />
                                                <Route path="commander" element={<Navigate to="/menu" replace />} />
                                                <Route
                                                    path="commander/create"
                                                    element={<Navigate to="/menu" replace />}
                                                />
                                                <Route path="mon-menu" element={<AppErrorBoundary><CreateMenuPage /></AppErrorBoundary>} />
                                                <Route path="*" element={<NotFound />} />
                                            </Route>
                                        </Routes>
                                    </HelmetProvider>
                                </BrowserRouter>
                                </ThemeProvider>
                            </CartProvider>
                        </ToastProvider>
                    </AppErrorBoundary>
                </QueryClientProvider>
            </MaintenanceGate>
        </React.StrictMode>
    );
}
